import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import SEO from "../components/SEO";
import { DistrictLevelSelect } from "../components/DistrictSelect";
import { getAllDistricts, getDistance } from "../data/koreaDistricts";

/* ── Tab icon SVGs (constant, no text) ── */
const TAB_ICONS = {
  rental: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" /></svg>
  ),
  general: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
  ),
  live: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.068.157 2.148.279 3.238.364.466.037.893.281 1.153.671L12 21l2.652-3.978c.26-.39.687-.634 1.153-.671 1.09-.085 2.17-.207 3.238-.364 1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>
  ),
  global: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>
  ),
};

/* ══════════════════════════════════════════════════
   Rental Calculator (from InquiryPage)
   ══════════════════════════════════════════════════ */
function RentalCalculator({ plans, onResult }) {
  const { t } = useI18n();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [customBW, setCustomBW] = useState(0);
  const [customColor, setCustomColor] = useState(0);

  useEffect(() => {
    if (plans.length > 0 && !selectedPlan) {
      const p = plans[0];
      setSelectedPlan(p);
      setCustomBW(p.bw_pages);
      setCustomColor(p.color_pages);
    }
  }, [plans, selectedPlan]);

  useEffect(() => {
    if (!selectedPlan) return;
    const base = selectedPlan.base_price;
    const extraBW = Math.max(0, customBW - selectedPlan.bw_pages);
    const extraColor = Math.max(0, customColor - selectedPlan.color_pages);
    const extraBWCost = Math.floor(extraBW / 1000) * selectedPlan.extra_bw_price;
    const extraColorCost = Math.floor(extraColor / 100) * selectedPlan.extra_color_price;
    const subtotal = base + extraBWCost + extraColorCost;
    const vat = Math.floor(subtotal * 0.1);
    const total = subtotal + vat;
    const result = {
      plan: selectedPlan, customBW, customColor,
      extraBW, extraColor, extraBWCost, extraColorCost,
      base, subtotal, vat, total,
      deposit: selectedPlan.deposit,
      contractYears: selectedPlan.contract_years,
    };
    onResult(result);
  }, [selectedPlan, customBW, customColor, onResult]);

  if (plans.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
      <h3 className="text-[15px] font-bold text-[#1d1d1f] flex items-center gap-2">
        <svg className="w-5 h-5 text-[#0071e3]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" /></svg>
        {t.consult_calcTitle}
      </h3>
      <div className="space-y-2">
        <p className="text-[12px] font-medium text-gray-500">{t.consult_planSelect}</p>
        <div className="grid gap-2">
          {plans.map((p, i) => (
            <button key={p.id} onClick={() => { setSelectedPlan(p); setCustomBW(p.bw_pages); setCustomColor(p.color_pages); }}
              className={`text-left p-3 rounded-xl border-2 transition ${selectedPlan?.id === p.id ? "border-[#0071e3] bg-blue-50/50" : "border-gray-200 hover:border-gray-300"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0071e3] text-white text-[11px] font-bold mr-2">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[13px] font-semibold text-[#1d1d1f]">{p.plan_name}</span>
                </div>
                <span className="text-[15px] font-bold text-[#0071e3]">{t.consult_monthly} {p.base_price.toLocaleString()}{t.consult_won}</span>
              </div>
              <div className="mt-1.5 ml-8 flex gap-3 text-[11px] text-gray-500">
                <span>{t.common_mono} {p.bw_pages.toLocaleString()}{t.consult_pages}</span>
                <span>{t.common_color} {p.color_pages.toLocaleString()}{t.consult_pages}</span>
                <span>{p.fax_option}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      {selectedPlan && (
        <div className="space-y-3 pt-2 border-t border-gray-100">
          <p className="text-[12px] font-medium text-gray-500">{t.consult_adjustPages}</p>
          <div>
            <div className="flex justify-between text-[12px] mb-1"><span className="text-gray-600">{t.consult_bwCount}</span><span className="font-bold text-[#1d1d1f]">{customBW.toLocaleString()}{t.consult_pages}</span></div>
            <input type="range" min={0} max={10000} step={1000} value={customBW} onChange={e => setCustomBW(Number(e.target.value))} className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0071e3]" />
            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5"><span>0</span><span className="text-[#0071e3]">{t.consult_default} {selectedPlan.bw_pages.toLocaleString()}</span><span>10,000</span></div>
          </div>
          <div>
            <div className="flex justify-between text-[12px] mb-1"><span className="text-gray-600">{t.consult_colorCount}</span><span className="font-bold text-[#1d1d1f]">{customColor.toLocaleString()}{t.consult_pages}</span></div>
            <input type="range" min={0} max={3000} step={100} value={customColor} onChange={e => setCustomColor(Number(e.target.value))} className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0071e3]" />
            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5"><span>0</span><span className="text-[#0071e3]">{t.consult_default} {selectedPlan.color_pages.toLocaleString()}</span><span>3,000</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Tab: 렌탈 견적
   ══════════════════════════════════════════════════ */
function RentalTab({ user, passedProduct, passedItems, passedDistrict, passedOptionSelections }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: user?.name || "", company: user?.company || "", phone: user?.phone || "",
    email: user?.email || "", inquiry_type: "rental",
    district: passedDistrict, message: buildInitialMessage(passedProduct, passedItems, passedDistrict, passedOptionSelections),
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [locating, setLocating] = useState(false);
  const [rentalProducts, setRentalProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(passedProduct?.id || "");
  const [rentalPlans, setRentalPlans] = useState([]);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const productSelectorRef = useRef(null);
  const [calcResult, setCalcResult] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/api/ikoffices-products").catch(() => ({ data: [] })),
      api.get("/api/store-products").catch(() => ({ data: [] })),
      api.get("/api/catalog?per_page=100").catch(() => ({ data: { items: [] } })),
    ]).then(([ikRes, storeRes, catalogRes]) => {
      const ikProds = (ikRes.data || []).filter(p => p.sale_type === "rental" || p.sale_type === "both");
      const storeProds = (storeRes.data || []);
      const catalogItems = (catalogRes.data?.items || []).map(c => ({
        id: `catalog-${c.id}`, name: c.product_name_spec || c.model_name, brand: c.brand, category: c.category,
        image_url: c.main_image || c.images?.[0], sale_type: "rental",
      }));
      // Merge all, dedup by name
      const names = new Set();
      const merged = [];
      for (const p of [...ikProds, ...storeProds, ...catalogItems]) {
        const key = (p.name || "").toLowerCase().trim();
        if (key && !names.has(key)) { names.add(key); merged.push(p); }
      }
      setRentalProducts(merged);
    });
  }, []);

  // Click-outside to close product selector
  useEffect(() => {
    const handle = (e) => { if (productSelectorRef.current && !productSelectorRef.current.contains(e.target)) setProductSearchOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filteredProducts = useMemo(() => {
    const q = productQuery.toLowerCase();
    return rentalProducts
      .filter(p => !q || (p.name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q))
      .slice(0, 50);
  }, [rentalProducts, productQuery]);

  useEffect(() => {
    if (selectedProductId) {
      api.get(`/api/rental-plans/${selectedProductId}`).then(r => setRentalPlans(r.data || [])).catch(() => setRentalPlans([]));
    } else { setRentalPlans([]); }
  }, [selectedProductId]);

  const handleGeolocate = useCallback(() => {
    if (!navigator.geolocation) { setError(t.consult_geoNotSupported); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const districts = getAllDistricts();
        let nearest = null, minDist = Infinity;
        for (const d of districts) {
          const dist = getDistance(latitude, longitude, d.lat, d.lng);
          if (dist < minDist) { minDist = dist; nearest = d; }
        }
        if (nearest) setForm(prev => ({ ...prev, district: nearest.name }));
        setLocating(false);
      },
      () => { setError(t.consult_geoError); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [t]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments(prev => [...prev, { url: data.url, filename: data.filename || file.name }]);
      }
    } catch { setError(t.consult_uploadError); }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    // 상담폼 3종은 실제 접수(+메일 알림)로 동작 — 카카오 가로채기 제외(2026-07-26).
    e.preventDefault();
    if (!form.name.trim()) { setError(t.consult_nameRequired); return; }
    if (!form.phone.trim() && !form.email.trim()) { setError(t.consult_phoneRequired); return; }
    setSubmitting(true); setError("");
    try {
      let fullMessage = form.message || "";
      if (calcResult) {
        const prod = rentalProducts.find(p => p.id === Number(selectedProductId));
        fullMessage = `[자동 견적]\n상품: ${prod?.name || ""}\n요금제: ${calcResult.plan?.plan_name || ""}\n흑백: ${calcResult.customBW.toLocaleString()}매, 컬러: ${calcResult.customColor.toLocaleString()}매\n월 렌탈료: ${calcResult.total.toLocaleString()}원 (VAT 포함)\n보증금: ${calcResult.deposit.toLocaleString()}원\n약정: ${calcResult.contractYears}년\n${form.district ? `설치지역: ${form.district}` : ""}\n\n${form.message || ""}`;
      }
      await api.post("/api/inquiries", { ...form, message: fullMessage, attachment_urls: attachments });
      if (calcResult) {
        const prod = rentalProducts.find(p => p.id === Number(selectedProductId));
        await api.post("/api/quotes/auto-generate", {
          title: `${prod?.name || "렌탈"} 견적서`, company: form.company, phone: form.phone,
          email: form.email, district: form.district, user_id: user?.id || 0,
          items: [{ product_name: prod?.name, product_id: selectedProductId, qty: 1, type: "rental" }],
          total_estimate: `월 ${calcResult.total.toLocaleString()}원 (VAT 포함)`,
          rental_details: { plan: calcResult.plan?.plan_name, base_price: calcResult.base, bw_pages: calcResult.customBW, color_pages: calcResult.customColor, subtotal: calcResult.subtotal, vat: calcResult.vat, total: calcResult.total, deposit: calcResult.deposit, contract_years: calcResult.contractYears },
          note: form.message,
        }).catch(() => {});
      }
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.error || t.consult_submitError); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-[600px] mx-auto text-center py-8">
        <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
        </div>
        <h2 className="text-2xl font-bold text-[#1d1d1f] mb-3">{t.consult_successTitle}</h2>
        <p className="text-apple-gray mb-2">{t.consult_successDesc}</p>
        {calcResult && (
          <div className="bg-[#f5f5f7] rounded-xl p-4 my-5 text-left max-w-sm mx-auto">
            <p className="text-[13px] font-semibold text-[#1d1d1f] mb-2">{t.consult_quoteSummary}</p>
            <p className="text-[12px] text-gray-600">{t.consult_monthlyRental}: <span className="font-bold text-[#0071e3]">{calcResult.total.toLocaleString()}{t.consult_won}</span> (VAT {"\uD3EC\uD568"})</p>
            <p className="text-[11px] text-gray-500 mt-1">{t.consult_autoQuoteNote}</p>
          </div>
        )}
        <p className="text-sm text-apple-lightgray mb-8">{t.consult_urgent} <a href="tel:1600-7542" className="text-apple-blue font-medium">1600-7542</a>{t.consult_callUs}</p>
        <div className="flex gap-3 justify-center">
          <Link to="/rental" className="px-6 py-3 bg-[#1d1d1f] text-white rounded-xl font-medium hover:bg-[#333] transition">{t.consult_nationalRental}</Link>
          <Link to="/" className="px-6 py-3 bg-apple-bg text-apple-black rounded-xl font-medium hover:bg-gray-200 transition">{t.consult_goHome}</Link>
        </div>
      </div>
    );
  }

  const F = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      {/* Left: Calculator + Info */}
      <div className="lg:col-span-2 space-y-5">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="text-[14px] font-bold text-[#1d1d1f] mb-3">{t.consult_selectProduct}</h3>
          <div ref={productSelectorRef} className="relative">
            {/* Selected product display / trigger button */}
            <button
              type="button"
              onClick={() => setProductSearchOpen(!productSearchOpen)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[13px] text-left flex items-center justify-between gap-2 hover:border-[#0071e3] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition bg-white"
            >
              {selectedProductId && rentalProducts.find(p => p.id === Number(selectedProductId)) ? (
                <span className="text-[#1d1d1f] truncate">
                  {(() => { const sp = rentalProducts.find(p => p.id === Number(selectedProductId)); return sp.brand ? `[${sp.brand}] ${sp.name}` : sp.name; })()}
                </span>
              ) : (
                <span className="text-gray-400">{t.consult_selectProductPlaceholder}</span>
              )}
              <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${productSearchOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </button>

            {/* Search dropdown */}
            {productSearchOpen && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden max-h-[360px] flex flex-col">
                <div className="p-3 border-b border-gray-100 flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                  <input
                    type="text"
                    placeholder={t.consult_searchProductPlaceholder || "제품명, 브랜드 검색..."}
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    className="w-full text-[13px] outline-none placeholder-gray-400"
                    autoFocus
                  />
                  {productQuery && (
                    <button type="button" onClick={() => setProductQuery("")} className="text-gray-300 hover:text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredProducts.length === 0 ? (
                    <div className="p-6 text-center text-[13px] text-gray-400">{t.search_noResults || "검색 결과가 없습니다"}</div>
                  ) : (
                    filteredProducts.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setSelectedProductId(String(p.id)); setProductSearchOpen(false); setProductQuery(""); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-left ${
                          String(p.id) === String(selectedProductId) ? 'bg-blue-50' : ''
                        }`}
                      >
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-contain bg-gray-50 flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 7.131A2.25 2.25 0 0016.5 9h-9a2.25 2.25 0 00-2.25-1.869" /></svg>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-gray-900 truncate">{p.brand ? `[${p.brand}] ` : ""}{p.name}</p>
                          <p className="text-[11px] text-gray-500">
                            {p.category || ""}
                            {p.rental_price > 0 ? ` · ${t.consult_monthly} ${Number(p.rental_price).toLocaleString()}${t.consult_won}~` : ""}
                          </p>
                        </div>
                        {String(p.id) === String(selectedProductId) && (
                          <svg className="w-4 h-4 text-[#0071e3] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Selected product image preview */}
          {selectedProductId && rentalProducts.find(p => p.id === Number(selectedProductId))?.image_url && (
            <div className="mt-3 bg-[#f5f5f7] rounded-xl p-3 flex items-center justify-center">
              <img src={rentalProducts.find(p => p.id === Number(selectedProductId)).image_url} alt="" className="max-h-[120px] object-contain" />
            </div>
          )}
        </div>
        {rentalPlans.length > 0 && <RentalCalculator plans={rentalPlans} onResult={setCalcResult} />}
        {passedProduct && !selectedProductId && (
          <div className="bg-[#f5f5f7] rounded-2xl p-5">
            <p className="text-[11px] font-medium text-gray-400 mb-2">{t.consult_inquiryProduct}</p>
            <div className="flex gap-3 items-center">
              {passedProduct.image_url && <img src={passedProduct.image_url} alt="" className="w-16 h-16 object-contain rounded-xl bg-white" />}
              <div>
                <p className="text-[14px] font-semibold text-[#1d1d1f] line-clamp-2">{passedProduct.name}</p>
                {passedProduct.rental_price > 0 && <p className="text-[13px] text-green-600 font-medium mt-0.5">{t.consult_monthly} {passedProduct.rental_price.toLocaleString()}{t.consult_won}~</p>}
              </div>
            </div>
          </div>
        )}
        <ContactInfo />
      </div>

      {/* Right: Form */}
      <div className="lg:col-span-3">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
          {user && (
            <div className="bg-blue-50 rounded-xl p-3 mb-5 text-sm text-blue-700 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
              {t.consult_autoFilled}
            </div>
          )}
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-xl p-3 mb-5">{error}</div>}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_name} <span className="text-red-400">*</span></label><input {...F("name")} required placeholder={t.consult_namePlaceholder} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_company}</label><input {...F("company")} placeholder={t.consult_companyPlaceholder} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_phone} <span className="text-red-400">*</span></label><input {...F("phone")} type="tel" placeholder="010-0000-0000" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_email}</label><input {...F("email")} type="email" placeholder="email@example.com" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[13px] font-medium text-[#1d1d1f]">{t.consult_installRegion}</label>
                <button type="button" onClick={handleGeolocate} disabled={locating}
                  className="text-[12px] text-[#0071e3] hover:text-blue-700 font-medium flex items-center gap-1 disabled:opacity-50 transition">
                  {locating ? (<><span className="animate-spin inline-block w-3 h-3 border-2 border-[#0071e3]/30 border-t-[#0071e3] rounded-full" /> {t.consult_locating}</>) : (<><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg> {t.consult_myLocation}</>)}
                </button>
              </div>
              <DistrictLevelSelect value={form.district || ""} onChange={e => setForm({ ...form, district: e.target.value })} className="border border-gray-300 rounded-xl px-3 py-2.5 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{calcResult ? t.consult_additionalRequest : t.consult_inquiryContent} <span className="text-red-400">{!calcResult ? "*" : ""}</span></label>
              <textarea {...F("message")} required={!calcResult} rows={5} placeholder={t.consult_contentPlaceholder} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition resize-none" />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_attachment}</label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-[#0071e3]/30 transition">
                <input type="file" multiple accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.hwp" onChange={handleFileUpload} disabled={uploading} className="hidden" id="rental-file-input" />
                <label htmlFor="rental-file-input" className="cursor-pointer">
                  <svg className="w-8 h-8 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" /></svg>
                  <p className="text-[12px] text-gray-500">{uploading ? t.consult_uploading : t.consult_uploadPrompt}</p>
                </label>
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#f5f5f7] rounded-lg px-3 py-2">
                      <span className="text-[12px] text-gray-700 truncate">{a.filename}</span>
                      <button type="button" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-[11px]">{t.consult_remove}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {calcResult && (
              <div className="bg-gradient-to-r from-[#0071e3] to-[#0058b0] rounded-xl p-4 text-white">
                <p className="text-[11px] text-white/70 mb-1">{t.consult_estimatedMonthly} (VAT {"\uD3EC\uD568"})</p>
                <p className="text-[24px] font-bold">{calcResult.total.toLocaleString()}{t.consult_won}<span className="text-[13px] font-normal text-white/80">/{t.consult_monthly}</span></p>
                <p className="text-[11px] text-white/60 mt-1">{t.common_mono} {calcResult.customBW.toLocaleString()}{t.consult_pages} + {t.common_color} {calcResult.customColor.toLocaleString()}{t.consult_pages} {"\xB7"} {"\uBCF4\uC99D\uAE08"} {calcResult.deposit.toLocaleString()}{t.consult_won} {"\xB7"} {calcResult.contractYears}{"\uB144"} {"\uC57D\uC815"}</p>
              </div>
            )}
            <button type="submit" disabled={submitting} className="w-full py-3.5 bg-[#0071e3] text-white rounded-xl text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2">
              {submitting ? t.consult_submitting : (<><svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg> {calcResult ? t.consult_requestQuote : t.consult_sendInquiry}</>)}
            </button>
            <p className="text-center text-[12px] text-gray-400">{t.consult_responseNote}</p>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Tab: 일반 상담
   ══════════════════════════════════════════════════ */
function GeneralTab({ user }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: user?.name || "", company: user?.company || "", phone: user?.phone || "",
    email: user?.email || "", inquiry_type: "etc", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    // 상담폼 3종은 실제 접수(+메일 알림)로 동작 — 카카오 가로채기 제외(2026-07-26).
    e.preventDefault();
    if (!form.name.trim()) { setError(t.consult_nameRequired); return; }
    if (!form.phone.trim() && !form.email.trim()) { setError(t.consult_phoneRequired); return; }
    if (!form.message.trim()) { setError(t.consult_contentRequired); return; }
    setSubmitting(true); setError("");
    try {
      await api.post("/api/inquiries", form);
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.error || t.consult_submitError); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-[600px] mx-auto text-center py-8">
        <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
        </div>
        <h2 className="text-2xl font-bold text-[#1d1d1f] mb-3">{t.consult_successTitle}</h2>
        <p className="text-apple-gray mb-6">{t.consult_successDesc}</p>
        <p className="text-sm text-apple-lightgray mb-8">{t.consult_urgent} <a href="tel:1600-7542" className="text-apple-blue font-medium">1600-7542</a>{t.consult_callUs}</p>
        <Link to="/" className="px-6 py-3 bg-[#1d1d1f] text-white rounded-xl font-medium hover:bg-[#333] transition">{t.consult_goHome}</Link>
      </div>
    );
  }

  const F = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <div className="lg:col-span-2 space-y-5">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
          <h3 className="text-[16px] font-bold text-[#1d1d1f] mb-2">{t.consult_generalTitle}</h3>
          <p className="text-[13px] text-gray-600 leading-relaxed">{t.consult_generalDesc}</p>
        </div>
        <ContactInfo />
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100 rounded-2xl p-5">
          <h4 className="text-[13px] font-bold text-purple-800 mb-1">{t.consult_sampleService}</h4>
          <p className="text-[12px] text-purple-600 leading-relaxed">{t.consult_sampleDesc}</p>
        </div>
      </div>
      <div className="lg:col-span-3">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
          {user && <div className="bg-blue-50 rounded-xl p-3 mb-5 text-sm text-blue-700 flex items-center gap-2"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>{t.consult_autoFilled}</div>}
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-xl p-3 mb-5">{error}</div>}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_name} <span className="text-red-400">*</span></label><input {...F("name")} required placeholder={t.consult_namePlaceholder} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_company}</label><input {...F("company")} placeholder={t.consult_companyPlaceholder} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_phone} <span className="text-red-400">*</span></label><input {...F("phone")} type="tel" placeholder="010-0000-0000" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_email}</label><input {...F("email")} type="email" placeholder="email@example.com" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_inquiryType}</label>
              <select {...F("inquiry_type")} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition">
                <option value="purchase">{t.consult_typePurchase}</option>
                <option value="toner">{t.consult_typeToner}</option>
                <option value="as">{t.consult_typeService}</option>
                <option value="etc">{t.consult_typeOther}</option>
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">{t.consult_inquiryContent} <span className="text-red-400">*</span></label>
              <textarea {...F("message")} required rows={6} placeholder={t.consult_generalPlaceholder} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition resize-none" />
            </div>
            <button type="submit" disabled={submitting} className="w-full py-3.5 bg-[#0071e3] text-white rounded-xl text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2">
              {submitting ? t.consult_submitting : (<><svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg> {t.consult_sendInquiry}</>)}
            </button>
            <p className="text-center text-[12px] text-gray-400">{t.consult_responseNote}</p>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Tab: 실시간 상담
   ══════════════════════════════════════════════════ */
function LiveChatTab({ user }) {
  const { t } = useI18n();
  const [chatOpen, setChatOpen] = useState(false);
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const lastMsgId = useRef(0);
  const scrollRef = useRef(null);

  const openChat = async () => {
    if (!user) return;
    setChatOpen(true); setLoading(true);
    try {
      const roomsRes = await api.get("/api/live-chat/rooms");
      let myRoom = roomsRes.data.find(r => r.status === "open");
      if (!myRoom) { myRoom = (await api.post("/api/live-chat/rooms", { subject: "1:1 \uC0C1\uB2F4" })).data; }
      setRoom(myRoom);
      const msgRes = await api.get(`/api/live-chat/rooms/${myRoom.id}/messages`);
      setMessages(msgRes.data);
      if (msgRes.data.length) lastMsgId.current = msgRes.data[msgRes.data.length - 1].id;
    } catch { }
    setLoading(false);
  };

  useEffect(() => {
    if (!chatOpen || !room) return;
    const iv = setInterval(() => {
      api.get(`/api/live-chat/rooms/${room.id}/messages?since_id=${lastMsgId.current}`).then(r => {
        if (r.data.length) { setMessages(prev => [...prev, ...r.data]); lastMsgId.current = r.data[r.data.length - 1].id; }
      });
    }, 2000);
    return () => clearInterval(iv);
  }, [chatOpen, room]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = () => {
    if (!input.trim() || !room) return;
    api.post(`/api/live-chat/rooms/${room.id}/messages`, { message: input.trim() }).then(() => setInput(""));
  };

  return (
    <div className="max-w-[800px] mx-auto">
      <p className="text-center text-[15px] text-gray-500 mb-8">{t.consult_liveIntro}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8 max-w-[600px] mx-auto">
        {/* KakaoTalk */}
        <a href="https://pf.kakao.com/_RPggG/chat" target="_blank" rel="noopener noreferrer"
          className="relative bg-[#FEE500] rounded-2xl p-6 flex flex-col items-center text-center hover:shadow-xl transition group">
          <span className="absolute top-3 right-3 text-[10px] font-bold text-white bg-red-500 px-2 py-0.5 rounded-full">{t.consult_recommended}</span>
          <div className="w-16 h-16 rounded-full bg-[#3C1E1E] flex items-center justify-center mb-4">
            <svg className="w-9 h-9 text-[#FEE500]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3C6.48 3 2 6.58 2 10.9c0 2.78 1.86 5.22 4.65 6.6-.15.54-.96 3.47-.99 3.69 0 0-.02.17.09.23.11.07.24.02.24.02.32-.04 3.68-2.42 4.26-2.84.56.08 1.14.12 1.75.12 5.52 0 10-3.58 10-7.9C22 6.58 17.52 3 12 3z"/></svg>
          </div>
          <h3 className="text-[16px] font-bold text-[#3C1E1E] mb-1">{t.consult_kakaoTitle}</h3>
          <p className="text-[12px] text-[#3C1E1E]/70 mb-3">{t.consult_kakaoDesc}</p>
          <span className="text-[13px] font-semibold text-[#3C1E1E] group-hover:underline">{t.consult_kakaoCta} &gt;</span>
        </a>

        {/* Phone */}
        <a href="tel:16007542"
          className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-6 flex flex-col items-center text-center hover:shadow-xl transition group">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
          </div>
          <h3 className="text-[16px] font-bold text-[#1d1d1f] mb-1">{t.consult_phoneTitle}</h3>
          <p className="text-[20px] font-bold text-emerald-600 mb-1">1600-7542</p>
          <p className="text-[12px] text-gray-500">{t.consult_businessHours}</p>
        </a>
      </div>

      {/* Inline chat area */}
      {chatOpen && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-[#0071e3] text-white px-5 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">{t.consult_chatHeader}</h3>
            <button onClick={() => setChatOpen(false)} className="text-white/70 hover:text-white"><svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
          </div>
          <div ref={scrollRef} className="h-[400px] overflow-y-auto px-4 py-3 space-y-2 bg-gray-50/50">
            {loading ? (
              <div className="flex items-center justify-center h-full"><div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div></div>
            ) : messages.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm text-[#86868b]">{t.consult_chatGreeting}</p>
                <p className="text-xs text-gray-400 mt-1">{t.consult_chatSubtext}</p>
              </div>
            ) : messages.map(m => (
              <div key={m.id} className={`flex ${m.is_admin ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.is_admin ? "bg-white text-[#1d1d1f] shadow-sm rounded-bl-md" : "bg-[#0071e3] text-white rounded-br-md"}`}>
                  {m.is_admin && <p className="text-[10px] text-[#0071e3] font-medium mb-0.5">{t.consult_adminLabel}</p>}
                  <p className="whitespace-pre-wrap">{m.message}</p>
                  <p className={`text-[9px] mt-0.5 ${m.is_admin ? "text-gray-400" : "text-white/50"}`}>{m.created_at?.slice(11, 16)}</p>
                </div>
              </div>
            ))}
          </div>
          {room?.status === "open" && (
            <div className="px-3 py-2 border-t bg-white">
              <div className="flex gap-2">
                <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} placeholder={t.consult_chatPlaceholder} className="flex-1 px-3 py-2 rounded-full border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-200 outline-none" />
                <button onClick={send} className="w-9 h-9 rounded-full bg-[#0071e3] text-white flex items-center justify-center hover:bg-[#0077ed] transition shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Tab: 해외 문의
   ══════════════════════════════════════════════════ */
function GlobalTab() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", country: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    // 상담폼 3종은 실제 접수(+메일 알림)로 동작 — 카카오 가로채기 제외(2026-07-26).
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) { setError(t.consult_allFieldsRequired); return; }
    setSubmitting(true); setError("");
    try {
      await api.post("/api/global-inquiry", { ...form, lang: navigator.language?.slice(0, 2) || "en" });
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.error || "Failed to submit."); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-[600px] mx-auto text-center py-8">
        <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
        </div>
        <h2 className="text-2xl font-bold text-[#1d1d1f] mb-3">Inquiry Submitted</h2>
        <p className="text-apple-gray mb-6">Thank you! We will respond to your inquiry within 1-2 business days.</p>
        <Link to="/" className="px-6 py-3 bg-[#1d1d1f] text-white rounded-xl font-medium hover:bg-[#333] transition">Home</Link>
      </div>
    );
  }

  const F = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <div className="lg:col-span-2 space-y-5">
        <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100 rounded-2xl p-6">
          <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>
          </div>
          <h3 className="text-[16px] font-bold text-[#1d1d1f] mb-2">Global Inquiry</h3>
          <p className="text-[13px] text-gray-600 leading-relaxed">For international inquiries about copier/printer rental, toner supply, and B2B partnerships. We welcome inquiries in English, Japanese, Russian, German, and other languages.</p>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0071e3]/10 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0071e3]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
            </div>
            <div><p className="text-[13px] text-gray-400">Email</p><p className="text-[14px] font-medium text-[#1d1d1f]">info@ikoffices.com</p></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0071e3]/10 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0071e3]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
            </div>
            <div><p className="text-[13px] text-gray-400">Phone</p><p className="text-[14px] font-medium text-[#1d1d1f]">+82-1600-7542</p></div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-3">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-xl p-3 mb-5">{error}</div>}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">Name <span className="text-red-400">*</span></label><input {...F("name")} required placeholder="Your name" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">Email <span className="text-red-400">*</span></label><input {...F("email")} type="email" required placeholder="email@example.com" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">Country</label><input {...F("country")} placeholder="Country" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
              <div><label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">Subject <span className="text-red-400">*</span></label><input {...F("subject")} required placeholder="Subject" className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition" /></div>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1d1d1f] mb-1.5">Message <span className="text-red-400">*</span></label>
              <textarea {...F("message")} required rows={6} placeholder="Please describe your inquiry in detail." className="w-full border border-gray-300 rounded-xl px-4 py-3 text-[14px] focus:ring-2 focus:ring-[#0071e3]/30 focus:border-[#0071e3] focus:outline-none transition resize-none" />
            </div>
            <button type="submit" disabled={submitting} className="w-full py-3.5 bg-[#0071e3] text-white rounded-xl text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2">
              {submitting ? "Submitting..." : (<><svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg> Submit Inquiry</>)}
            </button>
            <p className="text-center text-[12px] text-gray-400">We will respond within 1-2 business days.</p>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════ Shared: Contact Info ═══════════════════════ */
function ContactInfo() {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#0071e3]/10 rounded-xl flex items-center justify-center">
          <svg className="w-5 h-5 text-[#0071e3]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
        </div>
        <div><p className="text-[13px] text-gray-400">{t.consult_phoneConsult}</p><a href="tel:1600-7542" className="text-[17px] font-bold text-[#1d1d1f]">1600-7542</a></div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <div><p className="text-[13px] text-gray-400">{t.consult_consultHours}</p><p className="text-[14px] font-medium text-[#1d1d1f]">{t.consult_businessHours}</p></div>
      </div>
    </div>
  );
}

/* ═══════════════════════ Helpers ═══════════════════════ */
function buildInitialMessage(product, items, district, optionSelections) {
  const districtLine = district ? `설치 지역: ${district}\n` : "";
  const optLines = (optionSelections || []).length > 0
    ? `\n선택 옵션:\n${optionSelections.map(l => `  · ${l}`).join("\n")}\n`
    : "";
  if (product) {
    return `[렌탈 문의]\n상품명: ${product.name}\n${product.brand ? `브랜드: ${product.brand}\n` : ""}${product.rental_price > 0 ? `월 렌탈료: ${product.rental_price.toLocaleString()}원\n` : ""}${product.contract_period ? `계약기간: ${product.contract_period}\n` : ""}${districtLine}${optLines}\n추가 요청사항:\n`;
  }
  if (items.length > 0) {
    const lines = items.map((item) => `- ${item.product?.name || "제품"} (수량: ${item.quantity || 1})`);
    return `[견적 문의]\n\n문의 상품:\n${lines.join("\n")}\n\n추가 요청사항:\n`;
  }
  if (optLines) {
    return `[제품 문의]${optLines}\n추가 요청사항:\n`;
  }
  return "";
}

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════ */
export default function ConsultationPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const passedProduct = location.state?.product || null;
  const passedItems = location.state?.items || [];
  const passedDistrict = location.state?.district || searchParams.get("district") || "";
  const passedOptionSelections = location.state?.optionSelections || [];

  // Determine initial tab from URL params or passed state
  const initialTab = searchParams.get("tab") || (passedProduct ? "rental" : "rental");
  const [activeTab, setActiveTab] = useState(initialTab);

  const TABS = [
    { id: "rental", label: t.consult_tabRental, icon: TAB_ICONS.rental },
    { id: "general", label: t.consult_tabGeneral, icon: TAB_ICONS.general },
    { id: "live", label: t.consult_tabLive, icon: TAB_ICONS.live },
    { id: "global", label: t.consult_tabGlobal, icon: TAB_ICONS.global },
  ];

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <SEO
        title={t.consult_seoTitle}
        description={t.consult_seoDesc}
        path="/consultation"
      />

      {/* ── Dark Hero Banner ── */}
      <section className="relative bg-gradient-to-b from-[#0a0a12] to-[#1a1a2e] text-white min-h-[380px] flex items-center">
        <div className="max-w-5xl mx-auto px-6 pt-24 pb-16 text-center w-full">
          <h1 className="text-3xl md:text-[44px] font-bold tracking-tight leading-tight">{t.consult_heading}</h1>
          <p className="text-[16px] text-white/70 mt-3 max-w-xl mx-auto">{t.consult_subtitle}</p>
        </div>
      </section>

    <div className="max-w-[1100px] mx-auto px-4 pt-10 pb-16">

      {/* Tab Navigation */}
      <div className="flex overflow-x-auto gap-2 mb-8 pb-1 scrollbar-none justify-center" style={{ scrollbarWidth: "none" }}>
        {TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition ${
              activeTab === tab.id
                ? "bg-[#0071e3] text-white shadow-md"
                : "bg-[#f5f5f7] text-[#1d1d1f] hover:bg-gray-200"
            }`}>
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "rental" && <RentalTab user={user} passedProduct={passedProduct} passedItems={passedItems} passedDistrict={passedDistrict} passedOptionSelections={passedOptionSelections} />}
      {activeTab === "general" && <GeneralTab user={user} />}
      {activeTab === "live" && <LiveChatTab user={user} />}
      {activeTab === "global" && <GlobalTab />}
    </div>
    </div>
  );
}
